import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Warning, X } from "@phosphor-icons/react";

/**
 * Global banner shown in the whole app when a superadmin is impersonating
 * another user. Persistent, red, includes an "Exit" button that restores
 * the superadmin's JWT via the backend.
 */
export default function ImpersonationBanner() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    if (!user?._impersonating?.expires_at) return;
    const tick = () => {
      const secs = Math.max(0, Math.floor((new Date(user._impersonating.expires_at) - new Date()) / 1000));
      setRemaining(secs);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [user?._impersonating?.expires_at]);

  if (!user?._impersonating) return null;

  const stop = async () => {
    setBusy(true);
    try {
      await api.post("/superadmin/stop-impersonation");
      toast.success("Session superadmin restaurée");
      await refresh();
      window.location.href = "/superadmin/users";
    } catch (e) {
      toast.error("Impossible de quitter l'impersonation");
    } finally { setBusy(false); }
  };

  const mm = remaining != null ? Math.floor(remaining / 60) : "?";
  const ss = remaining != null ? String(remaining % 60).padStart(2, "0") : "??";

  return (
    <div className="w-full bg-[#E4572E] text-white px-6 py-2.5 flex items-center justify-between gap-4 flex-wrap sticky top-0 z-50 shadow-lg" data-testid="impersonation-banner">
      <div className="flex items-center gap-3 text-sm">
        <Warning size={20} weight="fill" />
        <span>
          <strong>Session impersonnée</strong> — vous naviguez en tant que{" "}
          <strong>{user.name || user.email}</strong> ({user.email}).
          Toutes vos actions sont tracées.
        </span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs font-mono opacity-90">expire dans {mm}:{ss}</span>
        <button
          onClick={stop} disabled={busy}
          className="bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition"
          data-testid="stop-impersonation-btn"
        >
          <X size={14} weight="bold" /> Quitter
        </button>
      </div>
    </div>
  );
}
